import React, { Fragment, useEffect, useRef, useState } from 'react';

interface DropdownProps {
  label?: string;
  labelClassname?: string;
  withInput?: boolean;
  icon?: React.ReactNode;
  text?: string;
  options: Array<{ id?: string; [key: string]: React.ReactNode } | React.ReactNode>;
  optionLabel?: string;
  onSelect: (option: { id?: string; [key: string]: React.ReactNode } | React.ReactNode) => void;
  selectedOption?: { id?: string; [key: string]: React.ReactNode } | string | React.ReactNode;
  placeholder?: string;
  size?: string;
  heightOption?: string;
  additionalClassname?: string;
  setModalHeight: (height: string) => void;
  setModalTruncated: (truncated: boolean) => void;
}

const Dropdown = ({
  label,
  labelClassname,
  withInput = false,
  icon,
  text,
  options,
  optionLabel,
  onSelect,
  selectedOption,
  placeholder,
  size = 'w-full',
  heightOption = 'max-h-[200px]',
  additionalClassname = '',
  setModalHeight,
  setModalTruncated,
}: DropdownProps) => {
  const selectRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (isDropdownOpen && selectRef.current && !selectRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
        setModalHeight('md:h-[644px]');
      }
    };
    // didmount dropdown
    document.addEventListener('mousedown', checkIfClickedOutside);

    // unmount dropdown
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleOptionSelect = (option) => {
    onSelect(option);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    if (isDropdownOpen) {
      setModalHeight('lg:max-h-full h-full');
      setModalTruncated(true);
    } else {
      setModalHeight('md:h-[644px]');
      setModalTruncated(false);
    }
  }, [isDropdownOpen]);

  return (
    <div className={`relative ${size}`} ref={selectRef}>
      {withInput ? (
        <Fragment>
          <label className={`${labelClassname}`}>{label}</label>
          <div onClick={toggleDropdown} className="flex items-center">
            <input
              type="text"
              value={selectedOption && (selectedOption?.[optionLabel] || selectedOption)}
              className={`w-full outline-none px-[15px] py-[8px] h-[40px] bg-reliableBlack3 text-reliableBlack border-b border-reliableBlack30 cursor-pointer placeholder:text-reliableBlack60 ${additionalClassname}`}
              placeholder={placeholder}
              readOnly
            />
            {/* Handle toggle icon */}
            {selectedOption ? (
              <span className="absolute right-[15px] text-red-500 cursor-pointer">
                <ArrowDropdown />
              </span>
            ) : (
              <span
                className={`absolute right-[15px] ${
                  isDropdownOpen
                    ? 'transform rotate-180 transition duration-100 text-reliableBlack80'
                    : 'text-reliableBlack80 transform rotate-0 duration-100'
                }`}
              >
                <ArrowDropdown />
              </span>
            )}
          </div>
        </Fragment>
      ) : (
        <div className="flex gap-1 items-center cursor-pointer h-10 justify-center px-2">
          {icon ? icon : <div>{text}</div>}

          <span style={{ rotate: isDropdownOpen ? '180deg' : '0deg' }}>
            <ArrowDropdown />
          </span>
        </div>
      )}
      {isDropdownOpen && (
        <ul
          className={`${size} absolute ${heightOption} overflow-y-auto z-10 left-0 text-reliableBlack80 bg-white shadow rounded border border-[#d5d1d9]`}
        >
          {options.length ? (
            options.map((option, index) => (
              <li
                id={`dd-geo-${(option?.[optionLabel] || option).toLowerCase().replace(/\s/g, '-')}`}
                key={option['id'] || index}
                className="px-4 py-2 hover:bg-[#f0eded] hover:text-[#615a5a] hover:pb-[9px] hover:pl-[15px] cursor-pointer"
                onClick={() => handleOptionSelect(option)}
              >
                {option?.[optionLabel] || option}
              </li>
            ))
          ) : (
            <li className="px-4 py-2">{placeholder}</li>
          )}
        </ul>
      )}
    </div>
  );
};

const ArrowDropdown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10">
    <path
      fill="#ADA6AE"
      fillRule="nonzero"
      d="M0 1.887L1.886 0l6.117 6.226L14.114 0 16 1.887 8.003 10z"
    />
  </svg>
);

export default Dropdown;
